import type { ReactNode } from "react";
import { Link, useLocation } from "react-router";
import { _ } from "i18n";
import { AvailabilityFilterValue } from "./availabilityFilterValue";

interface TableCellLinkProps {
	to: string;
	children: ReactNode;
}

/** Styled link for primary table name/label cells that navigate to the view page. */
export function TableCellLink({ to, children }: TableCellLinkProps) {
	const location = useLocation();
	const returnTo = `${location.pathname}${location.search}`;

	return (
		<Link className="datagrid-tableCellLink" to={to} state={{ returnTo }}>
			{children}
		</Link>
	);
}

/**
 * Normalizes API values that are boolean-like (booleans, 0/1, "true"/"false" strings).
 * Returns null when the value cannot be interpreted.
 */
export function normalizeBooleanLikeValue(value: unknown): boolean | null {
	if (value === null || value === undefined) {
		return null;
	}
	if (value === true || value === false) {
		return value;
	}
	if (value === 1 || value === "1") {
		return true;
	}
	if (value === 0 || value === "0") {
		return false;
	}
	if (typeof value === "string") {
		const s = value.trim().toLowerCase();
		if (s === "true") {
			return true;
		}
		if (s === "false") {
			return false;
		}
	}

	return null;
}

/**
 * Renders boolean values as colored indicators (Yes/No) for use in datagrid cells.
 * Shared across DiscoverGamesTable and all datagrid column configs.
 */
export function renderBoolean(value: unknown): ReactNode {
	const b = normalizeBooleanLikeValue(value);
	// fontWeight 700 keeps the colored value bold even where the cell weight is not inherited
	// (e.g. the read-only VIP cell's `thinCell` wrapper), matching the static datagrid cells.
	if (b === true) {
		return <span style={{ color: "green", fontWeight: 700 }}>{_("label.datagrid.yes")}</span>;
	}
	if (b === false) {
		return <span style={{ color: "red", fontWeight: 700 }}>{_("label.datagrid.no")}</span>;
	}
	return "-";
}

/**
 * Colors a boolean choice option (e.g. Yes/No) for use as a SelectComponent `formatOptionLabel`,
 * so editable boolean dropdowns keep the green/red highlighting of {@link renderBoolean}.
 */
export function renderBooleanChoiceLabel(option: unknown): ReactNode {
	const opt = option as { value?: unknown; label?: unknown };
	const label = typeof opt?.label === "string" ? opt.label : "";
	const b = normalizeBooleanLikeValue(opt?.value);
	// fontWeight matches the datagrid cell weight (Datagrid.css `font-weight: 700`), which the
	// react-select value does not inherit — so the editable dropdown looks like the static cells.
	if (b === true) {
		return <span style={{ color: "green", fontWeight: 700 }}>{label}</span>;
	}
	if (b === false) {
		return <span style={{ color: "red", fontWeight: 700 }}>{label}</span>;
	}
	return label;
}

/** Renders teamsheet/fixture availability (`online` | `offline` from API). */
export function renderOnlineOfflineAvailability(value: unknown): ReactNode {
	if (value === AvailabilityFilterValue.Online) {
		return _("admin.summary_tab.label.online");
	}
	if (value === AvailabilityFilterValue.Offline) {
		return _("admin.summary_tab.label.offline");
	}
	return "-";
}

/**
 * Renders percentage values (0–1 scale as 0–100% with one decimal).
 */
export function renderPercent(value: number | null | undefined): ReactNode {
	if (value == null) return "-";
	return `${(value * 100).toFixed(1)}%`;
}

/**
 * Renders numeric values with locale-aware formatting.
 */
export function renderNumber(value: number | null | undefined): ReactNode {
	if (value == null) return "-";
	return value.toLocaleString();
}

/**
 * Renders average days with one decimal and translated "days" suffix.
 */
export function renderAvgDays(value: number | null | undefined): ReactNode {
	if (value == null) return "-";
	return `${value.toFixed(1)} ${_("label.datagrid.days")}`;
}
